import React, { useEffect, useState, useCallback } from 'react';
import { apiGet, apiPost, apiPatch, apiDelete } from '../api/client';

// ─── Types ────────────────────────────────────────────────────────────────────
interface ApiKey {
  id:         string;
  name:       string;
  keyPrefix:  string;
  enabled:    boolean;
  lastUsedAt: string | null;
  expiresAt:  string | null;
  createdAt:  string;
}

interface CreatedKey extends ApiKey {
  key: string; // raw key — shown once
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconKey = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7.5" cy="15.5" r="5.5"/><path d="m21 2-9.6 9.6"/><path d="m15.5 7.5 3 3L22 7l-3-3"/>
  </svg>
);

const IconPlus = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M12 5v14M5 12h14"/>
  </svg>
);

const IconCopy = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const IconCheck = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function isExpired(iso: string | null): boolean {
  return !!iso && new Date(iso) < new Date();
}

// ─── New Key Modal ─────────────────────────────────────────────────────────────
interface ModalProps {
  onClose:  () => void;
  onCreated: (key: CreatedKey) => void;
}

const NewKeyModal: React.FC<ModalProps> = ({ onClose, onCreated }) => {
  const [name,      setName]      = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setLoading(true);
    setError(null);
    try {
      const created = await apiPost<CreatedKey>('/api/apikeys', {
        name: name.trim(),
        expiresAt: expiresAt || undefined,
      });
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create key');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: 420, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
        <div style={{ fontWeight: 700, fontSize: '16px', marginBottom: '20px', color: '#0f172a' }}>Generate API Key</div>

        <label style={labelStyle}>Name / Description</label>
        <input
          className="input-base"
          placeholder="e.g. CRM Integration"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          style={{ marginBottom: 14 }}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
        />

        <label style={labelStyle}>Expires At <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
        <input
          className="input-base"
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          style={{ marginBottom: 14 }}
        />

        {error && <div style={{ padding: '10px 12px', background: '#fee2e2', color: '#dc2626', borderRadius: '8px', fontSize: '13px', marginBottom: 14 }}>{error}</div>}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={loading} style={{ minWidth: 100 }}>
            {loading ? 'Creating…' : 'Generate Key'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Reveal Key Modal ──────────────────────────────────────────────────────────
const RevealKeyModal: React.FC<{ created: CreatedKey; onClose: () => void }> = ({ created, onClose }) => {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(created.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: 480, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a' }}>
            <IconCheck />
          </div>
          <div style={{ fontWeight: 700, fontSize: '16px', color: '#0f172a' }}>API Key Created</div>
        </div>
        <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px', lineHeight: 1.6 }}>
          Copy this key now — it <strong>will not be shown again</strong>.
        </p>

        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all', color: '#0f172a' }}>
          <span style={{ flex: 1 }}>{created.key}</span>
          <button
            onClick={copy}
            style={{ flexShrink: 0, background: copied ? '#dcfce7' : '#eff6ff', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', color: copied ? '#16a34a' : '#2563eb', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, transition: 'all .15s' }}
          >
            {copied ? <><IconCheck /> Copied!</> : <><IconCopy /> Copy</>}
          </button>
        </div>

        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: '#92400e', marginBottom: '20px' }}>
          Store this key securely. Pass it as the <code style={{ background: '#fef3c7', padding: '1px 4px', borderRadius: '3px' }}>X-API-Key</code> header in API requests.
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
};

// ─── Delete Confirm Modal ──────────────────────────────────────────────────────
const DeleteModal: React.FC<{ name: string; onConfirm: () => void; onClose: () => void; loading: boolean }> = ({ name, onConfirm, onClose, loading }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
    <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: 380, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
      <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '8px', color: '#0f172a' }}>Revoke API Key?</div>
      <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px', lineHeight: 1.6 }}>
        <strong>"{name}"</strong> will be permanently deleted. Any system using it will immediately lose access.
      </p>
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button className="btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
        <button
          onClick={onConfirm}
          disabled={loading}
          style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', fontWeight: 600, fontSize: '13px', cursor: 'pointer', opacity: loading ? .6 : 1 }}
        >
          {loading ? 'Revoking…' : 'Revoke Key'}
        </button>
      </div>
    </div>
  </div>
);

// ─── Shared styles ─────────────────────────────────────────────────────────────
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' };

// ─── Main Page ─────────────────────────────────────────────────────────────────
const ApiKeys: React.FC = () => {
  const [keys,       setKeys]       = useState<ApiKey[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createdKey, setCreatedKey] = useState<CreatedKey | null>(null);
  const [toDelete,   setToDelete]   = useState<ApiKey | null>(null);
  const [deleting,   setDeleting]   = useState(false);
  const [toggling,   setToggling]   = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<ApiKey[]>('/api/apikeys');
      setKeys(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreated = (key: CreatedKey) => {
    setShowCreate(false);
    setCreatedKey(key);
    setKeys((prev) => [{ id: key.id, name: key.name, keyPrefix: key.keyPrefix, enabled: key.enabled, lastUsedAt: key.lastUsedAt, expiresAt: key.expiresAt, createdAt: key.createdAt }, ...prev]);
  };

  const handleToggle = async (key: ApiKey) => {
    setToggling(key.id);
    try {
      const updated = await apiPatch<ApiKey>(`/api/apikeys/${key.id}/toggle`, {});
      setKeys((prev) => prev.map((k) => k.id === updated.id ? { ...k, enabled: updated.enabled } : k));
    } catch {
      // ignore
    } finally {
      setToggling(null);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await apiDelete(`/api/apikeys/${toDelete.id}`);
      setKeys((prev) => prev.filter((k) => k.id !== toDelete.id));
      setToDelete(null);
    } catch {
      // ignore
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ padding: '28px 32px', height: '100vh', overflowY: 'auto', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: 40, height: 40, borderRadius: '10px', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
            <IconKey />
          </div>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>API Keys</div>
            <div style={{ fontSize: '13px', color: '#64748b' }}>Manage access keys for external integrations</div>
          </div>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <IconPlus /> Generate Key
        </button>
      </div>

      {/* Info banner */}
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '12px 16px', marginBottom: '24px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '16px', flexShrink: 0, marginTop: 1 }}>ℹ️</span>
        <div style={{ fontSize: '13px', color: '#1e40af', lineHeight: 1.6 }}>
          External systems authenticate using the <code style={{ background: '#dbeafe', padding: '1px 5px', borderRadius: '4px', fontWeight: 600 }}>X-API-Key</code> header.
          Keys are hashed and cannot be retrieved after creation.
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: '12px', background: '#fee2e2', color: '#dc2626', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>{error}</div>
      )}

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {/* Table header */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 140px 140px 130px', padding: '10px 20px', background: '#f8fafc', borderBottom: '1px solid var(--border)', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em' }}>
          <span>Name / Prefix</span>
          <span>Status</span>
          <span>Last Used</span>
          <span>Expires</span>
          <span style={{ textAlign: 'right' }}>Actions</span>
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>Loading…</div>
        ) : keys.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '36px', marginBottom: '10px', opacity: .4 }}>🔑</div>
            <div style={{ fontWeight: 600, color: '#475569', marginBottom: '6px' }}>No API keys yet</div>
            <div style={{ fontSize: '13px', color: '#94a3b8' }}>Generate a key to allow external systems to access your APIs.</div>
          </div>
        ) : (
          keys.map((key, idx) => {
            const expired = isExpired(key.expiresAt);
            return (
              <div
                key={key.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 120px 140px 140px 130px',
                  padding: '14px 20px',
                  borderBottom: idx < keys.length - 1 ? '1px solid var(--border)' : 'none',
                  alignItems: 'center',
                  background: !key.enabled ? '#fafafa' : '#fff',
                  opacity: !key.enabled ? .7 : 1,
                  transition: 'background .15s',
                }}
              >
                {/* Name + prefix */}
                <div>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: '#0f172a', marginBottom: '3px' }}>{key.name}</div>
                  <code style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>{key.keyPrefix}…</code>
                </div>

                {/* Status toggle */}
                <div>
                  <button
                    onClick={() => handleToggle(key)}
                    disabled={toggling === key.id}
                    title={key.enabled ? 'Click to disable' : 'Click to enable'}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      padding: '4px 10px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                      fontSize: '11px', fontWeight: 700,
                      background: expired ? '#fee2e2' : key.enabled ? '#dcfce7' : '#f1f5f9',
                      color:      expired ? '#dc2626' : key.enabled ? '#16a34a' : '#64748b',
                      transition: 'all .15s',
                      opacity: toggling === key.id ? .6 : 1,
                    }}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: expired ? '#dc2626' : key.enabled ? '#16a34a' : '#94a3b8', display: 'inline-block' }} />
                    {expired ? 'Expired' : key.enabled ? 'Active' : 'Disabled'}
                  </button>
                </div>

                {/* Last used */}
                <div style={{ fontSize: '12px', color: '#64748b' }}>{fmtDateTime(key.lastUsedAt)}</div>

                {/* Expires */}
                <div style={{ fontSize: '12px', color: expired ? '#dc2626' : '#64748b' }}>
                  {key.expiresAt ? fmtDate(key.expiresAt) : 'Never'}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setToDelete(key)}
                    title="Revoke key"
                    style={{ background: 'transparent', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 8px', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center', transition: 'all .15s' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#fca5a5'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.background = '#fef2f2'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'transparent'; }}
                  >
                    <IconTrash />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modals */}
      {showCreate && <NewKeyModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />}
      {createdKey  && <RevealKeyModal created={createdKey} onClose={() => setCreatedKey(null)} />}
      {toDelete    && <DeleteModal name={toDelete.name} onConfirm={handleDelete} onClose={() => setToDelete(null)} loading={deleting} />}
    </div>
  );
};

export default ApiKeys;
